import { useEffect } from 'react';
import { Calendar, Download, ExternalLink, FileText, Image, Video } from 'lucide-react';

const Press = () => {
  useEffect(() => {
    document.title = 'Press - FemSportNet';
  }, []);

  const pressReleases = [
    {
      title: 'FemSportNet Raises $10M Series A to Expand Global Platform for Female Athletes',
      date: '2024-03-15',
      excerpt: 'Leading sports technology company secures funding to enhance platform features and expand into new markets.',
      link: '#'
    },
    {
      title: 'Partnership Announcement: FemSportNet Teams Up with Olympic Committee',
      date: '2024-02-28',
      excerpt: 'Strategic partnership aims to provide better support and visibility for female Olympic athletes.',
      link: '#'
    },
    {
      title: 'FemSportNet Launches Marketplace for Sports Equipment and Services',
      date: '2024-01-20',
      excerpt: 'New marketplace feature connects athletes with verified sports equipment vendors and professional services.',
      link: '#'
    },
    {
      title: 'Study Shows 300% Increase in Female Athlete Sponsorship Through FemSportNet',
      date: '2023-12-10',
      excerpt: 'Independent research highlights platform\'s impact on creating opportunities for female athletes.',
      link: '#'
    }
  ];

  const mediaKit = [
    {
      title: 'Company Logo Pack',
      description: 'High-resolution logos in various formats (PNG, SVG, EPS)',
      type: 'images',
      icon: <Image className="h-6 w-6" />,
      size: '2.3 MB'
    },
    {
      title: 'Brand Guidelines',
      description: 'Complete brand style guide including colors, fonts, and usage guidelines',
      type: 'document',
      icon: <FileText className="h-6 w-6" />,
      size: '1.8 MB'
    },
    {
      title: 'Product Screenshots',
      description: 'High-quality screenshots of platform features and interface',
      type: 'images',
      icon: <Image className="h-6 w-6" />,
      size: '5.2 MB'
    },
    {
      title: 'Executive Photos',
      description: 'Professional headshots of leadership team members',
      type: 'images',
      icon: <Image className="h-6 w-6" />,
      size: '3.1 MB'
    },
    {
      title: 'Company Video',
      description: 'Promotional video showcasing platform and mission',
      type: 'video',
      icon: <Video className="h-6 w-6" />,
      size: '45.7 MB'
    },
    {
      title: 'Fact Sheet',
      description: 'Key company statistics, milestones, and achievements',
      type: 'document',
      icon: <FileText className="h-6 w-6" />,
      size: '0.5 MB'
    }
  ];

  const mediaFeatures = [
    {
      outlet: 'TechCrunch',
      title: 'How FemSportNet is Revolutionizing Women\'s Sports',
      date: '2024-03-20',
      link: '#'
    },
    {
      outlet: 'ESPN',
      title: 'The Platform Giving Female Athletes a Voice',
      date: '2024-03-05',
      link: '#'
    },
    {
      outlet: 'Forbes',
      title: 'Female-Founded Startup Tackles Gender Gap in Sports',
      date: '2024-02-15',
      link: '#'
    },
    {
      outlet: 'Sports Illustrated',
      title: 'Digital Innovation in Women\'s Athletics',
      date: '2024-01-30',
      link: '#'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Press & Media
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Latest news, press releases, and media resources about FemSportNet and our mission 
              to empower female athletes worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Press Contact */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Media Inquiries
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Press Contact</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-1">Sarah Chen, CEO</p>
                  <p className="text-gray-600 dark:text-gray-300 mb-1">press@femsportnet.com</p>
                  <p className="text-gray-600 dark:text-gray-300">+1 (555) 123-4567</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Partnership Inquiries</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-1">Mónica Brandimarti, Head of Partnerships</p>
                  <p className="text-gray-600 dark:text-gray-300 mb-1">partnerships@femsportnet.com</p>
                  <p className="text-gray-600 dark:text-gray-300">+1 (555) 123-4568</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Press Releases
            </h2>
            
            <div className="space-y-6">
              {pressReleases.map((release, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-grow">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        {release.title}
                      </h3>
                      <div className="flex items-center text-gray-500 dark:text-gray-400 mb-3">
                        <Calendar size={16} className="mr-2" />
                        {new Date(release.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        {release.excerpt}
                      </p>
                    </div>
                    <a 
                      href={release.link}
                      className="ml-4 flex items-center text-green-500 hover:text-green-600 font-medium"
                    >
                      Read More
                      <ExternalLink size={16} className="ml-1" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Media Kit */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Media Kit
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mediaKit.map((item, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg mr-3">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {item.size}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                    {item.description}
                  </p>
                  <button className="flex items-center text-green-500 hover:text-green-600 font-medium">
                    <Download size={16} className="mr-2" />
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Media Coverage
            </h2>
            
            <div className="space-y-4">
              {mediaFeatures.map((feature, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                        {feature.title}
                      </h3>
                      <div className="flex items-center text-gray-500 dark:text-gray-400">
                        <span className="font-medium">{feature.outlet}</span>
                        <span className="mx-2">•</span>
                        <span>{new Date(feature.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <a 
                      href={feature.link}
                      className="flex items-center text-green-500 hover:text-green-600 font-medium"
                    >
                      Read Article
                      <ExternalLink size={16} className="ml-1" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Want to Feature FemSportNet?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            We're always happy to share our story and discuss the future of women's sports.
          </p>
          <button className="bg-white text-green-600 font-medium px-8 py-3 rounded-md hover:bg-green-50 transition-colors duration-200">
            Contact Our Press Team
          </button>
        </div>
      </section>
    </div>
  );
};

export default Press;